import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { EventsAPI, UploadAPI, resolveImageUrl } from "@/admin/api";
import { useAdmin } from "@/admin/store";
import { BtnGhost, BtnPrimary, Confirm, EmptyState, Field, Modal, PageHeader, SearchInput, StatusPill, TagChip, TagPicker, inputCls } from "@/components/admin/ui";

const FILTERS = ["All", "Upcoming", "Past", "Deleted"];
const empty = { eventTitle: "", eventDate: "", videoUrl: "", coverImagePath: "", description: "", tagIds: [] };

const fmt = (d) => {
  if (!d) return "—";
  const dateStr = d.includes("T") ? d.split("T")[0] : d;
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const Events = () => {
  const { log, user } = useAdmin();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("All");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [preview, setPreview] = useState("");
  const [errors, setErrors] = useState({});
  const [confirmDel, setConfirmDel] = useState(null);

  const today = new Date().toISOString().slice(0, 10);

  const { data: events = [], isLoading: eventsLoading } = useQuery({ queryKey: ["events"], queryFn: EventsAPI.getAll });
  const { data: eventTags = [], isLoading: tagsLoading } = useQuery({ queryKey: ["eventTags"], queryFn: EventsAPI.getTags });

  useEffect(() => {
    if (location.state?.add) {
      setForm(empty);
      setFileToUpload(null);
      setPreview("");
      setEditing("new");
    }
  }, [location.state]);

  const createMutation = useMutation({
    mutationFn: async (payload) => {
      let coverImagePath = payload.coverImagePath;
      if (fileToUpload) {
        coverImagePath = await UploadAPI.uploadFile(fileToUpload, "events");
      }
      const eventPayload = { ...payload, coverImagePath, createdBy: user?.userId || 1 };
      const newEvent = await EventsAPI.create(eventPayload);
      if (payload.tagIds.length > 0) {
        await EventsAPI.assignTags(newEvent.eventId || newEvent, payload.tagIds);
      }
      return newEvent;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["events"]);
      log("Event created", form.eventTitle);
      setEditing(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }) => {
      let coverImagePath = payload.coverImagePath;
      if (fileToUpload) {
        coverImagePath = await UploadAPI.uploadFile(fileToUpload, "events");
      }
      const eventPayload = { ...payload, coverImagePath };
      await EventsAPI.update(id, eventPayload);
      await EventsAPI.assignTags(id, payload.tagIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["events"]);
      log("Event updated", form.eventTitle);
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: EventsAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(["events"]);
      setConfirmDel(null);
    },
  });

  const tagName = (id) => eventTags.find((t) => t.tagId === id)?.tagName;
  
  const statusOf = (e) => {
    if (e.isDeleted) return "Deleted";
    if (!e.eventDate) return "Upcoming";
    const dateStr = e.eventDate.includes("T") ? e.eventDate.split("T")[0] : e.eventDate;
    return dateStr >= today ? "Upcoming" : "Past";
  };

  const rows = events.filter(
    (e) =>
      (filter === "All" || statusOf(e) === filter) &&
      (e.eventTitle || "").toLowerCase().includes(q.toLowerCase())
  );

  const openEdit = (e) => {
    setForm({
      eventTitle: e.eventTitle || "",
      eventDate: e.eventDate ? (e.eventDate.includes("T") ? e.eventDate.split("T")[0] : e.eventDate) : "",
      videoUrl: e.videoUrl || "",
      coverImagePath: e.coverImagePath || "",
      description: e.description || "",
      tagIds: e.tags?.map((t) => t.tagId) || [],
    });
    setPreview(resolveImageUrl(e.coverImagePath));
    setFileToUpload(null);
    setErrors({});
    setEditing(e.eventId);
  };

  const onFile = (f) => {
    if (f) {
      setFileToUpload(f);
      setPreview(URL.createObjectURL(f));
    }
  };

  const save = () => {
    const errs = {};
    if (!form.eventTitle.trim()) errs.title = "The event needs a name.";
    if (!form.eventDate) errs.date = "Choose a date.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    
    if (editing === "new") {
      createMutation.mutate(form);
    } else {
      updateMutation.mutate({ id: editing, payload: { ...form, isDeleted: false } });
    }
  };

  if (eventsLoading || tagsLoading) return <div className="p-8 text-cream/50">Loading events...</div>;

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div data-testid="admin-events">
      <PageHeader title="EVENTS" sub="Gatherings the house is preparing for.">
        <SearchInput value={q} onChange={setQ} placeholder="Search events" />
        <BtnPrimary data-testid="add-event-btn" onClick={() => { setForm(empty); setPreview(""); setFileToUpload(null); setErrors({}); setEditing("new"); }}>
          + Add Event
        </BtnPrimary>
      </PageHeader>

      <div className="mb-6 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`events-filter-${f.toLowerCase()}`} onClick={() => setFilter(f)}
            className={`border px-4 py-1.5 font-body text-[10px] tracking-[0.25em] uppercase transition-colors ${filter === f ? "border-brass text-brass" : "border-cream/15 text-cream/50"}`}>
            {f}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <EmptyState title="NO GATHERINGS YET" line="The courtyard is empty. Plan the first evening." actionLabel="+ Add Event" onAction={() => { setForm(empty); setPreview(""); setFileToUpload(null); setErrors({}); setEditing("new"); }} />
      ) : (
        <div className="border-t border-cream/10">
          {rows.map((e) => {
            const eventTagIds = e.tags?.map((t) => t.tagId) || [];
            return (
              <div key={e.eventId} data-testid={`event-row-${e.eventId}`} className="grid grid-cols-1 gap-4 border-b border-cream/10 py-5 md:grid-cols-12 md:items-center">
                <div className="md:col-span-2">
                  <div className="h-16 w-28 overflow-hidden border border-cream/10">
                    {e.coverImagePath ? <img src={resolveImageUrl(e.coverImagePath)} alt={e.eventTitle} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-bark" />}
                  </div>
                </div>
                <div className="md:col-span-3">
                  <p className="font-display text-lg leading-tight text-cream">{e.eventTitle}</p>
                  <p className="mt-1 font-body text-[10px] tracking-[0.25em] uppercase text-cream/40">{fmt(e.eventDate)}</p>
                </div>
                <div className="flex flex-wrap gap-1.5 md:col-span-2">
                  {eventTagIds.map((t) => <TagChip key={t}>{tagName(t)}</TagChip>)}
                </div>
                <div className="font-body text-xs font-light text-cream/50 md:col-span-2">
                  {e.createdByName || (e.createdBy ? `User ${e.createdBy}` : "System")}
                  <span className="block text-[10px] text-cream/35">{fmt(e.createdOn)}</span>
                </div>
                <div className="md:col-span-1">
                  <StatusPill tone={statusOf(e) === "Upcoming" ? "brass" : statusOf(e) === "Past" ? "cream" : "terra"}>
                    {statusOf(e)}
                  </StatusPill>
                </div>
                <div className="flex gap-3 md:col-span-2 md:justify-end">
                  <button data-testid={`event-edit-${e.eventId}`} onClick={() => openEdit(e)} className="font-body text-[10px] tracking-[0.2em] uppercase text-brass/80 hover:text-brass">Edit</button>
                  {!e.isDeleted ? (
                    <button data-testid={`event-delete-${e.eventId}`} onClick={() => setConfirmDel(e)} className="font-body text-[10px] tracking-[0.2em] uppercase text-terra/80 hover:text-terra">Delete</button>
                  ) : (
                    <button data-testid={`event-restore-${e.eventId}`} onClick={() => { updateMutation.mutate({ id: e.eventId, payload: { ...e, isDeleted: false } }); log("Event restored", e.eventTitle); }} className="font-body text-[10px] tracking-[0.2em] uppercase text-cream/60 hover:text-brass">Restore</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <Modal wide title={editing === "new" ? "ADD EVENT" : "EDIT EVENT"} onClose={() => setEditing(null)}>
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Event Title" error={errors.title}>
              <input data-testid="event-form-title" className={inputCls} value={form.eventTitle} onChange={(e) => setForm({ ...form, eventTitle: e.target.value })} />
            </Field>
            <Field label="Event Date" error={errors.date}>
              <input data-testid="event-form-date" type="date" className={inputCls} value={form.eventDate} onChange={(e) => setForm({ ...form, eventDate: e.target.value })} />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Video URL">
              <input className={inputCls} value={form.videoUrl} placeholder="https://" onChange={(e) => setForm({ ...form, videoUrl: e.target.value })} />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Cover Image">
              <label className="flex h-36 cursor-pointer items-center justify-center border border-dashed border-cream/20 bg-bark/50 transition-colors hover:border-brass/50">
                {preview ? (
                  <img src={preview} alt="Cover preview" className="h-full w-full object-cover" />
                ) : (
                  <span className="font-body text-[10px] tracking-[0.3em] uppercase text-cream/40">
                    Choose a photograph
                  </span>
                )}
                <input data-testid="event-form-cover" type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files[0])} />
              </label>
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Description">
              <textarea rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Event Tags">
              <TagPicker
                options={eventTags.filter((t) => !t.isDeleted).map(t => ({ id: t.tagId, name: t.tagName }))}
                selected={form.tagIds}
                onToggle={(id) =>
                  setForm((s) => ({ ...s, tagIds: s.tagIds.includes(id) ? s.tagIds.filter((t) => t !== id) : [...s.tagIds, id] }))
                }
              />
            </Field>
          </div>
          <div className="mt-8 flex justify-end gap-3">
            <BtnGhost onClick={() => setEditing(null)} disabled={isSaving}>Cancel</BtnGhost>
            <BtnPrimary data-testid="event-form-save" onClick={save} disabled={isSaving}>
              {isSaving ? "Saving..." : (editing === "new" ? "Add Event" : "Save Changes")}
            </BtnPrimary>
          </div>
        </Modal>
      )}
      {confirmDel && (
        <Confirm
          testId="event-delete-confirm"
          title="REMOVE THIS EVENT?"
          body={`“${confirmDel.eventTitle}” will no longer appear among the house's gatherings. You can restore it later.`}
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => {
            deleteMutation.mutate(confirmDel.eventId);
            log("Event removed", confirmDel.eventTitle);
          }}
        />
      )}
    </div>
  );
};

export default Events;
